<template>
  <div
    :id="accordionId"
    class="accordion-item"
    :class="[
      {
        'accordion-item--active': isActive,
        'accordion-item--fixed': fixedWidth,
        'accordion-item--left-padded': leftPadding,
        'accordion-item--landingpage': landingPage,
      },
      backgroundColor ? 'accordion-item--bg-' + backgroundColor : '',
    ]"
  >
    <suspense>
      <div
        :id="`accordion-item-headline-${accordionId}`"
        role="button"
        tabindex="0"
        :aria-label="headline"
        :aria-expanded="isActive"
        :aria-controls="`accordion-item-content-${accordionId}`"
        @click.prevent="handleClick"
        @keydown.enter="handleClick"
        @keydown.space.prevent="handleClick"
      >
        <component
          :is="headlineType"
          class="accordion-title"
          :class="{
            'accordion-title--secondary': secondary,
          }"
          :data-id="trackingId || null"
        >
          <slot name="headline">
            <span v-html="headline" />
          </slot>

          <span
            class="accordion-icon"
            role="presentation"
          >
            <span class="accordion-icon--vertical" />
            <span class="accordion-icon--horizontal" />
          </span>
        </component>
      </div>
    </suspense>

    <section
      v-if="!advice"
      v-show="isActive"
      :id="`accordion-item-content-${accordionId}`"
      :aria-labelledby="`accordion-item-headline-${accordionId}`"
      class="accordion-item-content"
    >
      <slot />
    </section>

    <section
      v-else-if="isActive && advice"
      :id="`accordion-item-content-${accordionId}`"
      :aria-labelledby="`accordion-item-headline-${accordionId}`"
      class="accordion-item-content"
    >
      <slot />
    </section>
  </div>
</template>

<script>
export default {
  props: {
    headline: {
      type: String,
      default: '',
    },
    headlineType: {
      type: String,
      default: 'h2',
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: null,
    },
    fixedWidth: {
      type: Boolean,
      default: null,
    },
    id: {
      type: String,
      default: '',
    },
    trackingId: {
      type: String,
      default: '',
    },
    advice: {
      type: Boolean,
    },
    backgroundColor: {
      type: String,
      default: null,
    },
    leftPadding: {
      type: Boolean,
      default: false,
    },
    landingPage: {
      type: Boolean,
      default: false,
    },
    disableScrollToItem: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle'],
  data: () => ({
    internalActive: false,
    enableScrollToItem: false,
  }),
  computed: {
    isActive() {
      if (this.active !== null) {
        return this.active;
      }

      return this.internalActive;
    },

    hash() {
      return this.$route.hash;
    },

    accordionId() {
      const randomId = `id-${Math.random().toString(16).slice(2)}`;
      return this.id ? this.id : randomId;
    },
  },
  watch: {
    internalActive(value) {
      if (value === true && (!this.disableScrollToItem || this.enableScrollToItem)) {
        this.scrollToElement();
      }
    },

    active(value) {
      if (value === true && (!this.disableScrollToItem || this.enableScrollToItem)) {
        this.scrollToElement();
      }
    },

    hash() {
      this.handleOpenByHash();
    },
  },
  mounted() {
    this.handleOpenByHash();
  },
  methods: {
    handleClick() {
      if (this.active !== null) {
        this.enableScrollToItem = true;
        this.$emit('toggle', !this.active);
        return;
      }

      this.internalActive = !this.internalActive;
    },

    handleOpenByHash() {
      if (!this.id) return;
      if (!this.$route.hash) return;
      if (this.$route.hash.replace('#', '') !== this.id) return;

      this.internalActive = true;
    },

    scrollToElement() {
      this.$el?.scrollIntoView({
        behavior: 'smooth',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.accordion-item {
  position: relative;
  display: block;
  border-top: 1px solid palette(grey, light);
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  scroll-margin-top: 112px;

  &.accordion-item--active {
    height: 100%;

    .accordion-title {
      color: $color-brand-fnmz-green;
      font-weight: bold;
      font-size: map-get($font-sizes, 'xl');

      &.accordion-title--secondary {
        color: palette(grey, dark);
        font-size: map-get($font-sizes, 'l');
        font-weight: normal;
      }
    }

    .accordion-icon {
      .accordion-icon--vertical {
        transition: all 0.3s ease-in;
        transform: rotate(90deg);
      }

      .accordion-icon--horizontal {
        opacity: 0;
        transition: all 0.3s ease-in;
        transform: rotate(90deg);
      }
    }

    :deep(.accordion-item-content) {
      cursor: auto;
      padding-bottom: $pad;

      @include bp(m) {
        font-size: map-get($font-sizes, 'l');
      }
    }
  }

  &:last-child {
    border-bottom: 1px solid palette(grey, light);
  }

  .accordion-title {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $pad $pad $pad 0;
    margin: 0;
    color: $color-action-text-neutral-default-base;
    font-size: map-get($font-sizes, 'xl');
    line-height: 34px;
    font-weight: normal;

    @include bp(m, max) {
      line-height: 22px;
    }

    &.accordion-title--secondary {
      font-size: map-get($font-sizes, 'l');
    }
  }

  .accordion-icon {
    position: relative;
    top: 2px;
    height: 16px;
    width: 16px;
    transition: transform 0.3s;
    transform: rotate(180deg);
    display: grid;
    align-items: center;

    .accordion-icon--horizontal,
    .accordion-icon--vertical {
      grid-row: 1;
      grid-column: 1;
      justify-self: center;
      background-color: palette(grey, dark);
      transition: all 0.3s ease-in;
      transform: rotate(-90deg);
    }

    .accordion-icon--horizontal {
      width: 16px;
      height: 2px;
      opacity: 1;
    }

    .accordion-icon--vertical {
      height: 16px;
      width: 2px;
    }
  }

  &.accordion-item--fixed {
    max-width: math.div($content-max-width, math.div($grid-total-columns, 10));
    margin: 0 auto;

    &.accordion-item--active {
      max-width: 100%;
      background-color: palette(sand, light);
      border-top: 0;

      .accordion-title {
        max-width: math.div($content-max-width, math.div($grid-total-columns, 10));
        margin: 0 auto;
        border-top: 1px solid palette(grey, light);
      }

      .accordion-item-content {
        max-width: math.div($content-max-width, math.div($grid-total-columns, 10));
        margin: 0 auto;
      }

      &.accordion-item--left-padded {
        .accordion-title,
        .accordion-item-content {
          padding-left: 20px;
        }
      }

      // Colors
      @each $palette, $tones in $palettes {
        @each $tone, $hex in $tones {
          @if $tone == 'base' {
            &.accordion-item--bg-#{$palette} {
              background-color: $hex;
            }
          } @else {
            &.accordion-item--bg-#{$palette}-#{$tone} {
              background-color: $hex;
            }
          }
        }
      }
    }

    &:last-child {
      border-bottom: 0;

      .accordion-title {
        border-bottom: 1px solid palette(grey, light);
      }

      &.accordion-item--active {
        .accordion-title {
          border-bottom: 0;
        }

        .accordion-item-content {
          border-bottom: 1px solid palette(grey, light);
        }
      }
    }
  }

  &.accordion-item--landingpage {
    &:first-child {
      border-top: 0;
    }

    .accordion-title {
      font-size: map-get($font-sizes, 'l');
      color: palette(black);
      font-weight: bold;
    }
  }
}
</style>
